"use client";
import { useScrollReveal } from "@/hooks/common/useScrollReveal";
import GcsImage from "@/components/common/GcsImage";
import { arrowYellow, underlineNavy } from "@/lib/gcsImagePaths";
import { underlineWhite } from "@/lib/gcsImagePaths";

export default function WhatWeBelieve() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section
      ref={ref}
      className={`py-16 md:py-24 bg-background transition-all duration-700 ${
        isVisible ? "animate-fade-in" : "opacity-0"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
            What We Believe
          </h2>

          <div className="space-y-12">
            <div
              ref={ref}
              className={`transition-all duration-700 ${
                isVisible
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 translate-x-20"
              }`}
            >
              <div className="flex flex-col md:flex-row md:flex-wrap items-center justify-center gap-3 md:gap-4 mb-6">
                <div className="bg-[#E2F3FB] px-6 py-3 inline-block">
                  <span className="text-xl md:text-2xl font-bold text-foreground">
                    <span className="relative inline-block">
                      Never
                      <GcsImage
                        path={underlineNavy}
                        alt="Decorative underline"
                        className="absolute -bottom-2 left-0 w-full h-auto"
                      />
                    </span>{" "}
                    Generic
                  </span>
                </div>
                <GcsImage
                  path={arrowYellow}
                  alt="arrow"
                  className="w-4 h-auto md:hidden animate-[slideDown_6s_ease-in-out_infinite] rotate-90"
                />
                <GcsImage
                  path={arrowYellow}
                  alt="arrow"
                  className="hidden md:block w-16 h-auto animate-[slide_6s_ease-in-out_infinite]"
                />
                <div className="bg-[#090739] px-6 py-3 inline-block">
                  <span className="text-xl md:text-2xl font-bold text-white">
                    <span className="relative inline-block">
                      Always
                      <GcsImage
                        path={underlineWhite}
                        alt="Decorative underline"
                        className="absolute -bottom-2 left-0 w-full h-auto"
                      />
                    </span>{" "}
                    Personal
                  </span>
                </div>
              </div>
              <p className="text-lg text-muted-foreground text-center">
                We start by understanding your needs and risk preferences to
                match you with the right protection.
              </p>
            </div>

            <div
              ref={ref}
              className={`transition-all duration-700 delay-400 ${
                isVisible
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 translate-x-20"
              }`}
            >
              <div className="flex flex-col md:flex-row md:flex-wrap items-center justify-center gap-3 md:gap-4 mb-6">
                <div className="bg-[#E2F3FB] px-6 py-3 inline-block">
                  <span className="text-xl md:text-2xl font-bold text-foreground">
                    <span className="relative inline-block">
                      Never
                      <GcsImage
                        path={underlineNavy}
                        alt="Decorative underline"
                        className="absolute -bottom-2 left-0 w-full h-auto"
                      />
                    </span>{" "}
                    Just One Option
                  </span>
                </div>
                <GcsImage
                  path={arrowYellow}
                  alt="arrow"
                  className="w-4 h-auto md:hidden animate-[slideDown_6s_ease-in-out_infinite] [animation-delay:1s] rotate-90"
                />
                <GcsImage
                  path={arrowYellow}
                  alt="arrow"
                  className="hidden md:block w-16 h-auto animate-[slide_6s_ease-in-out_infinite] [animation-delay:1s]"
                />
                <div className="bg-[#090739] px-6 py-3 inline-block">
                  <span className="text-xl md:text-2xl font-bold text-white">
                    <span className="relative inline-block">
                      Always
                      <GcsImage
                        path={underlineWhite}
                        alt="Decorative underline"
                        className="absolute -bottom-2 left-0 w-full h-auto"
                      />
                    </span>{" "}
                    Comparing
                  </span>
                </div>
              </div>
              <p className="text-lg text-muted-foreground text-center">
                We compare 50+ top-rated carriers across our Guardian
                Marketplace to find your best fit.
              </p>
            </div>

            <div
              ref={ref}
              className={`transition-all duration-700 delay-200 ${
                isVisible
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 translate-x-20"
              }`}
            >
              <div className="flex flex-col md:flex-row md:flex-wrap items-center justify-center gap-3 md:gap-4 mb-6">
                <div className="bg-[#E2F3FB] px-6 py-3 inline-block">
                  <span className="text-xl md:text-2xl font-bold text-foreground">
                    <span className="relative inline-block">
                      Never
                      <GcsImage
                        path={underlineNavy}
                        alt="Decorative underline"
                        className="absolute -bottom-2 left-0 w-full h-auto"
                      />
                    </span>{" "}
                    Paid to Sell
                  </span>
                </div>
                <GcsImage
                  path={arrowYellow}
                  alt="arrow"
                  className="w-4 h-auto md:hidden animate-[slideDown_6s_ease-in-out_infinite] [animation-delay:2s] rotate-90"
                />
                <GcsImage
                  path={arrowYellow}
                  alt="arrow"
                  className="hidden md:block w-16 h-auto animate-[slide_6s_ease-in-out_infinite] [animation-delay:2s]"
                />
                <div className="bg-[#090739] px-6 py-3 inline-block">
                  <span className="text-xl md:text-2xl font-bold text-white">
                    <span className="relative inline-block">
                      Always
                      <GcsImage
                        path={underlineWhite}
                        alt="Decorative underline"
                        className="absolute -bottom-2 left-0 w-full h-auto"
                      />
                    </span>{" "}
                    Unbiased
                  </span>
                </div>
              </div>
              <p className="text-lg text-muted-foreground text-center">
                Our zero-commission agents focus on saving you money, not
                selling more stuff.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
